/**
 * Rate card readings (TMMPDM-0086; one card since TMMPDM-0089). Counts and membership come from the
 * card the server sent. Nothing here sums subscribers, computes a margin or decides that a reseller
 * can sell.
 */
import type {
  AdminBandView,
  BandInput,
  CustomerRateCardVersionView,
  CustomerRateCardVersionWrite,
  CustomerRateCardView,
} from '@/api/tmm/types';

type Copy = (key: string, fallback?: string, values?: Record<string, string | number>) => string;

export function currentVersion(
  card: CustomerRateCardView,
): CustomerRateCardVersionView | undefined {
  if (card.currentVersionId !== null) {
    return card.versions.find((version) => version.id === card.currentVersionId);
  }
  return card.versions.find((version) => version.current);
}

/**
 * Version id → the card that owns it. OffersPanel names an offer's rate card from this, and the
 * subscription dialog resolves a move the same way. Later cards overwrite a repeated id.
 */
export function cardsByVersionId(
  cards: readonly CustomerRateCardView[],
): Map<number, CustomerRateCardView> {
  const byVersion = new Map<number, CustomerRateCardView>();
  for (const card of cards) {
    for (const version of card.versions) byVersion.set(version.id, card);
  }
  return byVersion;
}

/** Highest version number. The new-version body uses this only when nothing is current. */
export function latestVersion(card: CustomerRateCardView): CustomerRateCardVersionView | undefined {
  return [...card.versions].sort((a, b) => b.versionNo - a.versionNo)[0];
}

/** The newest DRAFT version, if any: the one the Prices tab edits by default. */
export function latestDraft(card: CustomerRateCardView): CustomerRateCardVersionView | undefined {
  return [...card.versions]
    .filter((version) => version.status === 'DRAFT')
    .sort((a, b) => b.versionNo - a.versionNo)[0];
}

/** The version a card's prices are read from: the current one, else the newest draft. */
export function displayVersion(
  card: CustomerRateCardView,
): CustomerRateCardVersionView | undefined {
  return currentVersion(card) ?? latestDraft(card) ?? latestVersion(card);
}

function bandInput(band: AdminBandView): BandInput {
  const input: BandInput = {
    from: band.from,
    flatMinor: band.flatMinor,
    perUnitMinor: band.perUnitMinor,
    wholesaleFlatMinor: band.wholesaleFlatMinor,
    wholesalePerUnitMinor: band.wholesalePerUnitMinor,
    costFlatMinor: band.costFlatMinor,
    costPerUnitMinor: band.costPerUnitMinor,
  };
  if (band.to !== null) input.to = band.to;
  return input;
}

/**
 * The version's own terms, sent explicitly so a new draft copies this version, not "latest".
 * TMMPDM-0088: the five AI wallet terms are copied; the superseded tax, tax label, grace and
 * recharge count (live settings since TMMAPI-0153) are never sent. TMMPDM-0089: our cost, wholesale
 * and both AI markups are copied too, and every band keeps its three prices.
 */
export function versionTerms(version: CustomerRateCardVersionView): CustomerRateCardVersionWrite {
  return {
    baseMinor: version.baseMinor,
    baseWholesaleMinor: version.baseWholesaleMinor,
    baseCostMinor: version.baseCostMinor,
    annualBaseMinor: version.annualBaseMinor ?? 0,
    annualBaseWholesaleMinor: version.annualBaseWholesaleMinor,
    trialDaysNewSubscriber: version.trialDaysNewSubscriber,
    trialDaysGuestConversion: version.trialDaysGuestConversion,
    aiWholesaleMarkupBp: version.aiWholesaleMarkupBp,
    aiRetailMarkupBp: version.aiRetailMarkupBp,
    aiFirstPurchaseMinor: version.aiFirstPurchaseMinor,
    aiRechargeTriggerMinor: version.aiRechargeTriggerMinor,
    aiRechargeAmountMinor: version.aiRechargeAmountMinor,
    aiMonthlyCreditMinor: version.aiMonthlyCreditMinor,
    aiCreditPlatformShareBp: version.aiCreditPlatformShareBp,
    seatBands: version.seatBands.map(bandInput),
    connectedAccountBands: version.connectedAccountBands.map(bandInput),
  };
}

/** A line's three per-unit prices. */
export interface UnitPrices {
  retailMinor: number;
  wholesaleMinor: number;
  costMinor: number;
}

/**
 * The "included + per unit" reading of a band table (TMMPDM-0089): exactly two bands, the first
 * `0..N` free at retail and wholesale (our cost per unit allowed, the same as the second band's),
 * the second `N+1..` open-ended with per-unit prices and no flat amounts. An empty table reads as
 * nothing included at no price. Any other shape is tiered: `null`, shown read-only and never
 * overwritten by the simple editor. This reads the table's shape; it prices nothing.
 */
export function simpleBands(
  bands: readonly AdminBandView[],
): { included: number; perUnit: UnitPrices } | null {
  if (bands.length === 0) {
    return { included: 0, perUnit: { retailMinor: 0, wholesaleMinor: 0, costMinor: 0 } };
  }
  if (bands.length !== 2) return null;
  const [free, paid] = bands;
  if (!free || !paid || free.from !== 0 || free.to === null || paid.to !== null) return null;
  if (paid.from !== free.to + 1) return null;
  const freeIsFree =
    free.flatMinor === 0 &&
    free.perUnitMinor === 0 &&
    free.wholesaleFlatMinor === 0 &&
    free.wholesalePerUnitMinor === 0 &&
    free.costFlatMinor === 0;
  const paidHasNoFlat =
    paid.flatMinor === 0 && paid.wholesaleFlatMinor === 0 && paid.costFlatMinor === 0;
  if (!freeIsFree || !paidHasNoFlat || free.costPerUnitMinor !== paid.costPerUnitMinor) return null;
  return {
    included: free.to,
    perUnit: {
      retailMinor: paid.perUnitMinor,
      wholesaleMinor: paid.wholesalePerUnitMinor,
      costMinor: paid.costPerUnitMinor,
    },
  };
}

/** The two bands the simple editor writes: `0..included` free, then the per-unit prices. */
export function simpleBandInputs(included: number, perUnit: UnitPrices): BandInput[] {
  return [
    {
      from: 0,
      to: included,
      flatMinor: 0,
      perUnitMinor: 0,
      wholesaleFlatMinor: 0,
      wholesalePerUnitMinor: 0,
      costFlatMinor: 0,
      costPerUnitMinor: perUnit.costMinor,
    },
    {
      from: included + 1,
      flatMinor: 0,
      perUnitMinor: perUnit.retailMinor,
      wholesaleFlatMinor: 0,
      wholesalePerUnitMinor: perUnit.wholesaleMinor,
      costFlatMinor: 0,
      costPerUnitMinor: perUnit.costMinor,
    },
  ];
}

export function versionChipLabel(versionNo: number, t: Copy): string {
  return t('customerRateCards.versionLabel', 'v{{version, number}}', { version: versionNo });
}
